import { useCallback } from "react";
import { message } from "antd";
import html2pdf from "html2pdf.js";
import { classicTemplate } from "../templates";
import type { InvoiceTemplateContext, PageSizeType } from "../templates";
import { TemplatesTypes } from "../templates";

type InvoiceConfig = InvoiceTemplateContext["config"];
type InvoiceOrder = InvoiceTemplateContext["order"];

interface UseInvoiceDocumentParams {
  order: InvoiceOrder | null;
  config: InvoiceConfig;
  pageSize: PageSizeType;
  template: TemplatesTypes;
}

const formatInvoiceAmount = (value: unknown) => {
  const numericValue = Number.parseFloat(String(value));
  return Number.isNaN(numericValue) ? "0.00" : numericValue.toFixed(2);
};

const sanitizeFileName = (value: unknown) => {
  const name = String(value || "invoice")
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
    .trim();

  return name || "invoice";
};

const removeFrame = (frame: HTMLIFrameElement) => {
  if (frame.parentNode) {
    frame.parentNode.removeChild(frame);
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read PDF blob."));
    reader.readAsDataURL(blob);
  });

export const useInvoiceDocument = ({
  order,
  config,
  pageSize,
  template,
}: UseInvoiceDocumentParams) => {
  const getInvoiceHtml = useCallback(
    (isForPrint = false) => {
      if (!order) {
        return "";
      }

      const context = {
        order,
        config,
        pageSize,
        isForPrint,
        fmt: formatInvoiceAmount,
      };

      return classicTemplate(context);
    },
    [config, order, pageSize],
  );

  const printInvoice = useCallback(() => {
    if (!order) {
      return;
    }

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      removeFrame(frame);
      return;
    }

    doc.open();
    doc.write(getInvoiceHtml(true));
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => removeFrame(frame), 1000);
    }, 500);
  }, [getInvoiceHtml, order]);

  const downloadInvoice = useCallback(() => {
    if (!order) {
      return;
    }

    const container = document.createElement("div");
    container.innerHTML = getInvoiceHtml(true);

    html2pdf()
      .from(container)
      .set({
        filename: `Invoice_${sanitizeFileName(order.order_no)}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .save();

    message.success("Invoice PDF downloaded!");
  }, [getInvoiceHtml, order]);

  const getInvoicePdfBase64 = useCallback(async () => {
    if (!order) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = getInvoiceHtml(true);

    const blob = await html2pdf()
      .from(container)
      .set({
        filename: `Invoice_${sanitizeFileName(order.order_no)}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .outputPdf("blob");

    return await blobToBase64(blob);
  }, [getInvoiceHtml, order]);

  const silentPrint = useCallback(() => {
    if (!order) return;

    const frame = document.createElement("iframe");
    frame.style.cssText =
      "position:absolute;top:-9999px;left:-9999px;width:1px;height:1px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      removeFrame(frame);
      return;
    }

    doc.open();
    doc.write(getInvoiceHtml(true));
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => removeFrame(frame), 2000);
    }, 600);
  }, [getInvoiceHtml, order]);

  return {
    getInvoiceHtml,
    printInvoice,
    silentPrint,
    downloadInvoice,
    getInvoicePdfBase64,
  };
};
